<template>
	<v-dialog
		v-if="dialog && !fkResolved"
		v-model="dialog"
		persistent
		max-width="1024px"
	>
		<v-card>
			<v-card-title>
				<v-row no-gutters justify="center">
					<span>
						{{ matchCount }}
						<span v-if="matchCount === 1">{{ ' ' }}record</span
						><span v-else>{{ ' ' }} records </span> match your
						chosen search criteria:
					</span>
				</v-row>
				<v-row no-gutters justify="center">
					<v-chip-group>
						<v-chip
							v-for="(formField, index) in foreignKey.FormFields"
							:key="index"
						>
							{{ formField.Label }} :
							{{ getFormFieldRecordValue(formField) }}
						</v-chip>
					</v-chip-group>
				</v-row>
				<v-card-subtitle v-if="!fkResolved">
					<v-row no-gutters justify="center">
						<span>
							Either search and select from the list below OR
							<v-btn class="primary" @click="onCreateNew()"
								>Create new</v-btn
							>
							for a new record using your chosen values.
						</span>
					</v-row>
				</v-card-subtitle>
			</v-card-title>

			<div class="grid-flex-container">
				<div class="grid-toolbar">
					<quick-filter
						:Columns="fields"
						:filter="quickFilter"
						@filterChanged="onQuickFilterChanged"
					/>
					<v-badge
						:value="advancedFilter.FiltersCount > 0"
						left
						class="d-none d-lg-flex"
						bordered
						overlap
					>
						<template v-slot:badge>
							<span style="color: white">{{
								advancedFilter.FiltersCount
							}}</span>
						</template>
						<v-btn color="primary" @click="onShowAdvancedFilter">
							<v-icon class="mr-1">mdi-magnify</v-icon>
							Advanced search
						</v-btn>
					</v-badge>
				</div>
				<div class="generic-grid-container">
					<grid-view
						v-if="gridView"
						:loadingData="loading"
						:gridData="gridData"
						:irisUserMetadata="irisUserMetadata"
						:visibleColumns="fields"
						:page="page"
						:rowsPerPage="rowsPerPage"
						:totalCount="totalCount"
						:sorting="sorting"
						:hasActions="false"
						@rowsPerPageChanged="onRowsPerPageChanged"
						@pageChanged="onPageChanged"
						@sortChanged="onSortChanged"
						@rowClick="onRowClicked"
					>
					</grid-view>
				</div>
			</div>
			<v-card-actions>
				<v-spacer />
				<v-btn @click="close()">Cancel</v-btn>
			</v-card-actions>
		</v-card>
		<filters-dialog
			v-if="showAdvancedFilter"
			:dialog="showAdvancedFilter"
			:filter="advancedFilter"
			:columns="fields"
			@filterChanged="onAdvancedFilterChanged"
		/>
	</v-dialog>
</template>
<script>
import GridView from '../../shared/gridViews/GridView.vue';
import QuickFilter from '../../shared/gridViews/QuickFilter.vue';
import FiltersDialog from '../../shared/gridViews/FiltersDialog.vue';
import {
	GeneralActions,
	InventoryFormFormActions,
} from '../../../store/actions';
import { InventoryFormFormGetters } from '../../../store/getters';
import { mapGetters } from 'vuex';

import dataFieldUtils from '../../../utils/dataFieldUtils';
import gridViewFieldsUtils from '../../../utils/gridViewFieldsUtils';
import { filterOperators } from '../../../enums/filterOperators';
export default {
	name: 'CheckLinkFieldsModal',
	components: { GridView, QuickFilter, FiltersDialog },
	props: {
		dialog: Boolean,
		gridView: Object,
		foreignKey: Object,
	},
	data() {
		return {
			initialized: false,
			fkResolved: true,
			loading: false,
			rowsPerPage: 10,
			page: 1,
			sorting: [],
			quickFilter: { FieldId: null, Value: '' },
			advancedFilter: { Operator: 1, Filters: [] },
			gridData: [],
			irisUserMetadata: [],
			totalCount: 0,
			matchCount: 0,
			showAdvancedFilter: false,
		};
	},
	computed: {
		...mapGetters({
			record: [InventoryFormFormGetters.RECORD],
		}),
		currentInventoryFormId() {
			return this.$route.params.formId;
		},

		fields() {
			if (!this.gridView) return [];
			return this.gridView.Columns.filter((c) => c.IsVisible);
		},

		itemKeys() {
			if (!this.foreignKey) return [];
			if (!this.foreignKey.FormFields) return [];
			let keys = [];

			this.foreignKey.FormFields.forEach((ff) => {
				ff.GridViewFields.forEach((x) => {
					let recordKey = gridViewFieldsUtils.getFieldName(x);
					let correspondingKey = null;
					let correspondingColumn = this.getFkCorrespondingColumn(
						x.DataFieldId
					);
					if (correspondingColumn) {
						correspondingKey =
							gridViewFieldsUtils.getFieldName(
								correspondingColumn
							);
					}
					keys.push({
						recordKey: recordKey,
						correspondingKey: correspondingKey,
						gvf: x,
					});
				});
			});

			return keys;
		},
	},

	async mounted() {
		this.$store.dispatch(GeneralActions.LOADING, true);
		this.advancedFilter = this.getAdvancedFilterForFk(
			this.foreignKey,
			this.gridView
		);
		await this.loadData();
		this.matchCount = this.totalCount;
		if (this.matchCount > 0) {
			if (this.recordMatch(this.gridData[0])) {
				this.onSubmit(this.gridData[0]);
			} else {
				this.fkResolved = false;
			}
		} else {
			this.advancedFilter = { Operator: 1, Filters: [] };
			await this.loadData();
			this.fkResolved = false;
		}
		this.initialized = true;
		this.$store.dispatch(GeneralActions.LOADING, false);
	},
	methods: {
		getFormFieldRecordValue(formField) {
			if (!formField) return null;
			if (!formField.GridViewFields) return null;
			if (formField.GridViewFields.length === 1) {
				let key = gridViewFieldsUtils.getFieldName(
					formField.GridViewFields[0]
				);
				return this.record[key];
			} else {
				let separator = formField.Separator || '/';
				let values = [];
				formField.GridViewFields.forEach((gvf) => {
					let key = gridViewFieldsUtils.getFieldName(gvf);
					values.push(this.record[key]);
				});
				return values.join(separator);
			}
		},

		loadData() {
			return new Promise((resolve) => {
				this.loading = true;
				let columns = [];
				if (this.fields.length > 0) {
					columns = this.fields.map((x) => x.Id);
				}

				let payload = {
					ExcludeId: false,
					ViewId: this.gridView.Id,
					RowsPerPage: this.rowsPerPage,
					Page: this.page,
					Columns: columns,
					IncludeCount: true,
					InventoryFormId: this.currentInventoryFormId,
				};

				if (this.sorting.length > 0) payload.Sorting = this.sorting;
				if (this.advancedFilter.Filters.length > 0 || this.filter) {
					payload.AdvancedFilter = this.getAdvancedFilter();
					this.advancedFilter.FiltersCount =
						this.advancedFilter.Filters.length;
				}
				if (this.quickFilter.Value)
					payload.QuickFilter = this.quickFilter;

				this.$store
					.dispatch(
						InventoryFormFormActions.LOAD_GRID_VIEW_DATA,
						payload
					)
					.then((resp) => {
						if (resp) {
							this.gridData = resp.Data;
							this.irisUserMetadata = resp.IrisUsers;
							this.totalCount = resp.Count;
						}
						this.loading = false;
						resolve();
					});
			});
		},

		close() {
			this.$emit('close');
		},
		onSubmit(item) {
			let keys = [];
			let idKey = dataFieldUtils.getDataFieldName(
				this.foreignKey.LinkField
			);
			keys.push({ key: idKey, value: item.Id });
			this.itemKeys.forEach((x) => {
				if (
					x.recordKey &&
					x.correspondingKey &&
					item[x.correspondingKey]
				) {
					keys.push({
						key: x.recordKey,
						value: item[x.correspondingKey],
					});
				}
			});

			this.$store
				.dispatch(InventoryFormFormActions.UPDATE_RECORD_KEY, keys)
				.then(() => {
					this.$emit('submit');
				});
		},

		onCreateNew() {
			let keys = [];
			let idKey = dataFieldUtils.getDataFieldName(
				this.foreignKey.LinkField
			);
			keys.push({ key: idKey, value: -1 });

			this.$store
				.dispatch(InventoryFormFormActions.UPDATE_RECORD_KEY, keys)
				.then(() => {
					this.$emit('submit');
				});
		},
		onShowAdvancedFilter() {
			this.showAdvancedFilter = true;
		},

		recordMatch(item) {
			let result = true;
			this.itemKeys.every((x) => {
				if (x.recordKey && x.correspondingKey) {
					if (item[x.correspondingKey] && this.record[x.recordKey]) {
						result =
							item[x.correspondingKey] ===
							this.record[x.recordKey];
					} else if (
						!item[x.correspondingKey] &&
						!this.record[x.recordKey]
					) {
						result = true;
					} else {
						result = false;
					}
				} else {
					return false;
				}
				return result;
			});
			return result;
		},

		getAdvancedFilter() {
			let result = this.advancedFilter;
			if (this.filter) result.Filters.push(this.filter);
			return result;
		},

		onRowsPerPageChanged(value) {
			this.rowsPerPage = value;
			this.loadData();
		},
		onPageChanged(value) {
			this.page = value;
			this.loadData();
		},
		onSortChanged(value) {
			this.sorting = value;
			this.page = 1;
			this.loadData();
		},
		onQuickFilterChanged(value) {
			this.quickFilter = value;
			this.page = 1;
			this.loadData();
		},
		onAdvancedFilterChanged(value) {
			this.advancedFilter = value;
			this.page = 1;
			this.loadData();
			this.showAdvancedFilter = false;
		},
		onRowClicked(value) {
			this.onSubmit(value);
		},

		getAdvancedFilterForFk() {
			let advancedFilter = { Operator: 1, Filters: [] };
			this.foreignKey.FormFields.forEach((f) => {
				f.GridViewFields.forEach((gvf) => {
					let key = gridViewFieldsUtils.getFieldName(gvf);
					let correspondingColumn = this.getFkCorrespondingColumn(
						gvf.DataFieldId
					);
					if (this.record[key]) {
						let filter = {
							Value: this.record[key],
							Operator: filterOperators.Equal,
							FieldId: correspondingColumn.Id,
						};
						advancedFilter.Filters.push(filter);
					} else {
						let filter = {
							Operator: filterOperators.IsBlank,
							FieldId: correspondingColumn.Id,
						};
						advancedFilter.Filters.push(filter);
					}
				});
			});

			if (advancedFilter.Filters.length == 0) return null;
			return advancedFilter;
		},
		getFkCorrespondingColumn(dataFieldId) {
			if (!this.gridView) return null;
			if (!dataFieldId) return null;
			let gvf = this.gridView.Columns.find(
				(x) => x.DataFieldId == dataFieldId
			);
			if (!gvf) return null;
			return gvf;
		},
	},
};
</script>
<style lang="scss">
.v-data-table > .v-data-table__wrapper > table tr {
	cursor: pointer;
}
</style>
